import '@zebra-fed/icons/Enterprise/index.css';
import Snackbar from '@material-ui/core/Snackbar';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  EditRounded as EditIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';

// Internal imports
import { AuthContext } from '../../authentication/Auth';
import { fetchDevices, getTotalCount, fetchLastPage, getAppSettings } from './kioskDeviceQueries';
import { useDGAMLicenseCheck } from '../../authentication/licensing';
import NoAccessMessage from "../../components/common/NoAccessMessage";

import { ROLES, RESOURCES, usePermissions, withRBAC } from '../../authentication/rbac';

// Components
import BottomNavLinks from '../../components/BottomNavLinks';
import NumberToDate from '../users/components/NumberToDate';
import Toolbar from '../../components/common/Toolbar';
import Table from '../../components/common/Table';

import {
  ContentWrapper,
  CommonPageHeader,
  CommonPageTitle,
  SnackbarContent,
  CheckingAccessMessage,
} from '../../styled-components/shared';
import KioskDevice from '../../components/modals/KioskDevice';

const MainContent = styled.div`
  border-bottom: none;
  border: 1px solid #e0e3e9;
  margin-top: 10px;
  @media screen and (max-width: 750px) {
    margin-top: 60px;
  }
`;

const CuzCell = styled.p`
  font-size: 14px;
  font-weight: 400;
  overflow: hidden;
  white-space: nowrap;
  line-height: 20px;
  padding: 0px 0px 0px 16px;;
  -o-text-overflow: ellipsis;
  -ms-text-overflow: ellipsis;
  text-overflow: ellipsis;
`;

const CustomHeader = styled.p`
  padding: 0px 0px 0px 16px;
  font-weight: bold;
  text-wrap-mode: nowrap;
  align-self: 'center';
  text-align: ${props => (props.left ? 'left' : 'center')};
`;

const DEFAULT_ROWS_PER_PAGE = 10;
const DEFAULT_ORDER_BY = 'lastUpdatedAt'
const DEFAUTL_ORDER_DIRECTION = 'desc'

function KioskDevicePage() {
  const [data, setData] = useState([]);
  const [error, setError] = useState(false);
  const [snackbarVisible, setSnackBarVisible] = useState(false);
  const [snackbarMessage, setSnackBarMessage] = useState('');
  const [pageSize, setPageSize] = useState(DEFAULT_ROWS_PER_PAGE);
  const [currentPage, setCurrentPage] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [searchField, setSearchField] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [orderBy, setOrderBy] = useState(DEFAULT_ORDER_BY);
  const [orderDirection, setOrderDirection] = useState(DEFAUTL_ORDER_DIRECTION);
  const [cursors, setCursors] = useState(null);
  const context = useContext(AuthContext);
  const { t } = useTranslation();
  const [selectedDevice, setSelectedDevice] = useState(null);
  const deviceFormModalRef = useRef();
  const { isDGAMLicensed, loadingLicense } = useDGAMLicenseCheck();

  const { 
    canReadKioskDevice,
    canCreateKioskDevice,
    canUpdateKioskDevice,
    canDeleteKioskDevice,
    canSendAlarm,
    canMarkDeviceReCommissioned,
    canMarkDeviceDeCommissioned,
    canMarkDeviceLost,
    canMarkDeviceFound,
    canUpdateAlias,
  } = usePermissions();

  useEffect(() => {
    if (!searchText) {
      setOrderBy(DEFAULT_ORDER_BY);
      setSearchField(null);
    }else{
      setOrderBy(searchField);
    }
  }, [searchText, searchField]);

  async function getData(currentPageSize, orderBy = DEFAULT_ORDER_BY, orderDirection = DEFAUTL_ORDER_DIRECTION, shouldRefreshCount = false, currentSearchText = searchText, currentSearchField = searchField) {
    setIsLoading(true);
    try {
      setCurrentPage(0);      
  
      const { devices, cursors: newCursors, totalCount } = await fetchDevices({
        context,
        limit: currentPageSize,
        searchField: currentSearchField,
        searchText: currentSearchText,
        orderBy,
        orderDirection,
        totalCount: itemCount
      });
  
      setData(devices);
      setCursors(newCursors);
      setItemCount(totalCount);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setIsLoading(false);
    }
  }

  async function initManageDevices() {
    const appSettings = await getAppSettings(context);
    setLoggedInUser(appSettings?.SSOActivationStatus === true 
      ? context.currentUser.uid 
      : context.currentUser.email
    );
    getData(pageSize, orderBy, orderDirection);
  }

  useEffect(() => {
    if (!canReadKioskDevice) {
      return; // TODO: redirect/show unauthorized message
    }
    initManageDevices();
  }, []);


  async function next() {
    if (!cursors?.last) return;

    setIsLoading(true);
    setCurrentPage(prev => prev + 1);

    try {
      const { devices, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAfterDoc: cursors.last,
        totalCount: itemCount
      });

      if (devices.length > 0) {
        setData(devices);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching next page:', error);
      setIsLoading(false);
    }
  }

  async function prev() {
    if (!cursors?.first) return;

    setIsLoading(true);
    setCurrentPage(prev => prev - 1);

    try {
      const { devices, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        endBeforeDoc: cursors.first,
        totalCount: itemCount
      });

      if (devices.length > 0) {
        setData(devices);
        setCursors(newCursors);
      }
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching previous page:', error);
      setIsLoading(false);
    }
  }

  async function handleFirstPage() {
    setIsLoading(true);
    setCurrentPage(0);
    try {
      const { devices, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount,
        isFirstPage: true
      });

      setData(devices);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching first page:', error);
      setIsLoading(false);
    }
  }

  async function handleLastPage() {
    setIsLoading(true);
    try {
      const { devices, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        totalCount: itemCount,
        isLastPage: true
      });
  
      if (devices.length > 0) {
        setData(devices);
        setCursors(newCursors);
  
        const totalPages = Math.ceil(itemCount / pageSize);
        const lastPageNumber = Math.max(0, totalPages - 1);
        setCurrentPage(lastPageNumber);
      } else {
        setData([]);
        setCursors(null);
        setCurrentPage(0);
      }
  
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching last page:', error);
      setData([]);
      setCursors(null);
      setCurrentPage(0);
      setIsLoading(false);
    }
  }

  function handleSearch(text, field) {
    setSearchText(text);
    setSearchField(field);
    handleFetchWithSearch(text, field);
  }

  function handleClearSearch() {
    setSearchText('');
    setSearchField(null);
    getData(pageSize, DEFAULT_ORDER_BY, orderDirection, true, '', null);
  }

  async function handleRefresh() {
    setIsLoading(true);
    try {
      const { devices, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: searchField || orderBy,
        orderDirection,
        startAtDoc: cursors?.first,
        totalCount: itemCount
      });
  
      setData(devices);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error refreshing devices:', error);
      setIsLoading(false);
    }
  }

  async function handleFetchWithSearch(text, field) {
    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null);
  
    try {
      const { devices, cursors: newCursors, totalCount } = await fetchDevices({
        context,
        limit: pageSize,
        searchText: text,
        searchField: field,
        orderBy: field,
        orderDirection
      });

      setData(devices);
      setCursors(newCursors);
      setIsLoading(false);
      setItemCount(totalCount);
    } catch (error) {
      console.error('Error searching devices:', error);
      setIsLoading(false);
      setItemCount(0);
      setData([]);
    }
  }

  async function handleSort(field, direction) {
    if (searchText && field !== searchField) return;

    setIsLoading(true);
    setCurrentPage(0);
    setCursors(null); // Reset cursors on sort change

    try {
      setOrderBy(field);
      setOrderDirection(direction);

      const { devices, totalCount, cursors: newCursors } = await fetchDevices({
        context,
        limit: pageSize,
        searchField,
        searchText,
        orderBy: field,
        orderDirection: direction
      });

      setData(devices);
      setItemCount(totalCount);
      setCursors(newCursors);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching devices:', error);
      setIsLoading(false);
    }
  }

  function onModalClose() {
    setError(false);
    setSnackBarMessage(t('messages.success.success18'));
    setSnackBarVisible(true);
    getData(pageSize, orderBy, orderDirection, true);
  }


  function onDeviceCreated() {
    handleRefresh()
  }
  function onDeviceUpdated() {
    handleRefresh()
  }

  // Table Columns Configuration
  const columns = [
    {
      title: <CustomHeader left>{t('deviceDetail.deviceName')}</CustomHeader>,
      field: '__name__',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.__name__ }</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('deviceSerialNumber')}</CustomHeader>,
      field: 'serialNumber',
      searchable: true,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.serialNumber}</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('alias')}</CustomHeader>,
      field: 'friendlyName',
      searchable: true,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.friendlyName}</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('kioskName')}</CustomHeader>,
      field: 'kioskName',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.kioskName}</CuzCell>
      ),
    },
    {
      title: <CustomHeader left>{t('siteNameText')}</CustomHeader>,
      field: 'siteName',
      searchable: false,
      sorting: true,
      render: rowData => (
        <CuzCell>{rowData.siteName}</CuzCell>
      ),
    },


    {
      title: <CustomHeader>{t('lastStatusUpdate')}</CustomHeader>,
      field: 'lastUpdatedAt',
      sorting: true,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell>
          <NumberToDate
            style={{ fontSize: '14px' }}
            timestamp={rowData.lastUpdatedAt}
          />
        </CuzCell>
      ),
    },

    {
      title: <CustomHeader>{t('status')}</CustomHeader>,
      field: 'deviceStatusText',
      sorting: false,
      cellStyle: { textAlign: 'center' },
      render: rowData => (
        <CuzCell style={{ color: rowData?.deviceStatusText === 'Missing' ? '#D70015' : '#00864F' }}>
          {rowData?.deviceStatusText}
        </CuzCell>
      ),
    },
  ];


  const handleEdit = (deviceData) => {
    setSelectedDevice(deviceData);
    deviceFormModalRef.current.toggleDialog();
  };
  

  return (
    <ContentWrapper
      style={{
        cursor: loadingLicense ? "wait" : "default",
        opacity: loadingLicense ? "0.4" : "1",
      }}
    >
      <CommonPageHeader>
        <CommonPageTitle>{t('menu.subMenu.manageKioskDevice')}</CommonPageTitle>
      </CommonPageHeader>

      {!isDGAMLicensed && !loadingLicense && (
        <NoAccessMessage>
          {t('licenseUpgradeText')}
        </NoAccessMessage>
      )}

      {isDGAMLicensed && !loadingLicense && (
        <>
          <Toolbar
            searchText={searchText}
            onSearch={(searchText, searchField) => {
              handleSearch(searchText, searchField);
            }}
            onClear={() => {
              handleClearSearch();
            }}
            multiSearchOpts={[
              { name: t('deviceSerialNumber'), value: 'serialNumber' },
              { name: t('alias'), value: 'friendlyName' }
            ]}
            placeholderText={`${t('search')} ${t('menu.subMenu.manageKioskDevice')}`}
          />

          <MainContent>
            <Table
              columns={columns}
              data={data}
              isLoading={isLoading}
              orderBy={orderBy}
              orderDirection={orderDirection}
              isSearchActive={!!searchText}
              searchField={searchField}
              onSort={(field, direction) =>  {
                handleSort(field, direction)
              }}
              pagination={{
                itemCount,
                currentPage,
                rowsPerPage: pageSize,
                rowsPerPageOptions: [10, 20, 50],
                onNextPageClicked: next,
                onPrevPageClicked: prev,
                onFirstPageClicked: handleFirstPage,
                onLastPageClicked: handleLastPage,
                onChangeRowsPerPage: (event) => {
                  const newSize = event.target.value;
                  setPageSize(newSize);
                  setCursors(null);
                  getData(newSize, orderBy, orderDirection, false, searchText, searchField);
                },
                hidePagination: itemCount <= DEFAULT_ROWS_PER_PAGE
              }}
              actions={[
                (rowData => ({
                  hidden: !canUpdateKioskDevice,
                  icon: () => <EditIcon />,
                  onClick: (event, rowData) => handleEdit(rowData),
                  tooltip: t('edit'),
                }))
              ].filter(Boolean)}
              emptyMessage={t('messages.error.err26')}
            />
          </MainContent>
        </>
      )}

      <BottomNavLinks />
      
        <KioskDevice
          modalRef={deviceFormModalRef}
          initialData={selectedDevice}
          onClose={() => {
              onModalClose();
          }}
          onDeviceCreated={onDeviceCreated}
          onDeviceUpdated={onDeviceUpdated}
        />

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        open={snackbarVisible}
        autoHideDuration={5000}
        onClose={() => setSnackBarVisible(false)}
      >
        <SnackbarContent error={error}>{snackbarMessage}</SnackbarContent>
      </Snackbar>
    </ContentWrapper>
  );
}

export default withRBAC(KioskDevicePage, RESOURCES.KIOSK_DEVICE);
